export const riderServiceCategories = ['food', 'grocery', 'pharmacy', 'parcel', 'other'] as const;

export type PaymentDestinationDraft = {
  name: string;
  number: string;
  instructions: string;
  qr: string;
};

export type PaymentDestinationRow = {
  gcash_account_name: string | null;
  gcash_number: string | null;
  instructions: string | null;
  qr_storage_path: string | null;
};

export type RiderCapacityDraft = {
  id: string;
  kg: string;
  packages: string;
  size: string;
  canFund: boolean;
  fundingLimitPhp: string;
  capabilities: string[];
};

export type RiderCapacityRow = {
  id: string;
  user_id: string;
  full_name: string | null;
  vehicle_type: string;
  vehicle_plate: string | null;
  max_load_weight_grams: number | null;
  max_package_count: number | null;
  max_package_size: string | null;
  can_fund_purchases: boolean | null;
  approved_funding_limit_centavos: number | null;
  service_capabilities: string[] | null;
};

export const emptyPaymentDestinationDraft = (): PaymentDestinationDraft => ({ name: '', number: '', instructions: '', qr: '' });
export const emptyRiderCapacityDraft = (): RiderCapacityDraft => ({ id: '', kg: '', packages: '', size: 'medium', canFund: false, fundingLimitPhp: '0', capabilities: [] });

export function riderCapacityOptionLabels(rows: RiderCapacityRow[]) {
  const normalizedNames = rows.map((row) => row.full_name?.trim() ?? '');
  const nameCounts = normalizedNames.reduce<Record<string, number>>((counts, name) => {
    if (name) counts[name.toLocaleLowerCase()] = (counts[name.toLocaleLowerCase()] ?? 0) + 1;
    return counts;
  }, {});
  return Object.fromEntries(rows.map((row, index) => {
    const name = normalizedNames[index];
    const shortId = row.user_id.slice(0, 8);
    return [row.id, name
      ? `${name}${(nameCounts[name.toLocaleLowerCase()] ?? 0) > 1 ? ` · ${shortId}` : ''}`
      : `Unnamed rider · ${shortId}`];
  }));
}

export function paymentDestinationDraft(row: PaymentDestinationRow): PaymentDestinationDraft {
  return {
    name: row.gcash_account_name ?? '',
    number: row.gcash_number ?? '',
    instructions: row.instructions ?? '',
    qr: row.qr_storage_path ?? '',
  };
}

export function riderCapacityDraft(row: RiderCapacityRow): RiderCapacityDraft {
  return {
    id: row.id,
    kg: row.max_load_weight_grams == null ? '' : String(row.max_load_weight_grams / 1000),
    packages: row.max_package_count == null ? '' : String(row.max_package_count),
    size: row.max_package_size ?? 'medium',
    canFund: row.can_fund_purchases === true,
    fundingLimitPhp: row.approved_funding_limit_centavos == null ? '0' : String(row.approved_funding_limit_centavos / 100),
    capabilities: [...(row.service_capabilities ?? [])],
  };
}

export function validatePaymentDestination(draft: PaymentDestinationDraft) {
  const errors: Partial<Record<keyof PaymentDestinationDraft, string>> = {};
  if (draft.name.trim().length < 2) errors.name = 'Enter the GCash account name (at least 2 characters).';
  if (draft.number.trim().length < 5) errors.number = 'Enter the GCash destination number (at least 5 characters).';
  return errors;
}

export function validateRiderCapacity(draft: RiderCapacityDraft) {
  const errors: Partial<Record<'id' | 'kg' | 'packages' | 'capabilities' | 'fundingLimitPhp', string>> = {};
  const kg = Number(draft.kg);
  const packages = Number(draft.packages);
  if (!draft.id) errors.id = 'Select an approved rider.';
  if (!draft.kg.trim() || !Number.isFinite(kg) || kg <= 0 || kg > 100) errors.kg = 'Enter a maximum load from 0.001 to 100 kg.';
  if (!draft.packages.trim() || !Number.isInteger(packages) || packages < 1 || packages > 100) errors.packages = 'Enter a whole package count from 1 to 100.';
  if (!draft.capabilities.length) errors.capabilities = 'Select at least one reviewed service capability.';
  const funding = Number(draft.fundingLimitPhp);
  if (draft.canFund && (!draft.fundingLimitPhp.trim() || !Number.isFinite(funding) || funding <= 0 || funding > 50000)) errors.fundingLimitPhp = 'Enter an approved advance limit from PHP 0.01 to PHP 50,000.';
  if (!draft.canFund && draft.fundingLimitPhp.trim() && funding !== 0) errors.fundingLimitPhp = 'Funding limit must be zero when funding is declined.';
  return errors;
}

export function samePaymentDestinationDraft(left: PaymentDestinationDraft, right: PaymentDestinationDraft) {
  return left.name === right.name && left.number === right.number && left.instructions === right.instructions && left.qr === right.qr;
}

export function sameRiderCapacityDraft(left: RiderCapacityDraft, right: RiderCapacityDraft) {
  return left.id === right.id && left.kg === right.kg && left.packages === right.packages && left.size === right.size && left.canFund === right.canFund && left.fundingLimitPhp === right.fundingLimitPhp && left.capabilities.length === right.capabilities.length && left.capabilities.every((value) => right.capabilities.includes(value));
}

export function isCurrentCapacityResponse(currentRiderId: string, currentSelectionRevision: number, response: { riderId: string; selectionRevision: number }) {
  return currentRiderId === response.riderId && currentSelectionRevision === response.selectionRevision;
}

export function actionableSaveError(subject: string, error: unknown) {
  const detail = error instanceof Error ? error.message : typeof error === 'object' && error && 'message' in error ? String(error.message) : String(error);
  if (detail.startsWith('The server accepted the save')) return detail;
  return `${subject} was not saved. ${detail || 'Check the connection and try again.'}`;
}
